import {
    BIG_ZERO,
    ClearingHouse,
    getTickerSymbol,
    PositionDraft,
    PositionDraftDataUpdatable,
    PositionSide,
    SDKError,
} from "@perp/sdk-curie"
import Big from "big.js"
import { Referral } from "container/Referral"
import { utils } from "ethers"
import { debounce } from "lodash-es"
import segmentService from "module/analytics/service/SegmentService"
import { AppNetwork } from "module/network/container/AppNetwork"
import { useNotification } from "module/notification/hook/useNotification"
import { OpenPositionService } from "module/otcMaker/OpenPositionService"
import { OtcMakerClient } from "module/otcMaker/OtcMakerClient"
import { Slippage } from "module/settings/container/slippage"
import { Web3WalletContainer } from "module/wallet/container/Web3WalletContainer"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMarket } from "sdk-react/markets/useMarket"
import { usePerpConnectedEffect } from "sdk-react/perpetualProtocol/usePerpConnectedEffect"
import { usePerpInitializedEffect } from "sdk-react/perpetualProtocol/usePerpInitializedEffect"
import { usePerpTransactionSender } from "sdk-react/perpetualProtocol/usePerpTransactionSender"
import { Handlers } from "sdk-react/tools/useHandlers"
import { TxLoading } from "sdk-react/tools/useTxLoading"
import { TxNotifiers } from "sdk-react/tools/useTxNotifiers"
import { trackOpenPositionButtonClicked } from "service/segment/tracks"
import { createContainer } from "unstated-next"
import { toWei } from "util/format"
import { isUserDeniedSignature } from "util/is"
import { strToBig } from "./utils"

interface PositionOpenInitialState {
    baseAssetSymbol: string
    sideDefault?: PositionSide
}

interface OpenPositionParams {
    isUsingOtcEntryPrice: boolean
    positionDraft: PositionDraft
    slippage: Big
    referralCode?: string
}

const QUOTE_TOKEN_SYMBOL = "USD"
const DEFAULT_SIDE = PositionSide.LONG
const DEFAULT_LAST_EDIT_BASE = false
const DEFAULT_BUYING_POWER_RATIO = 0

const DEBOUNCE_SECONDS = 0.3

export const PositionOpen = createContainer(usePositionOpen)

function usePositionOpen({ baseAssetSymbol, sideDefault = DEFAULT_SIDE }: PositionOpenInitialState) {
    // NOTE: don't use useTickerSymbolFromRouterParams. react router provider can't work well when using portal (our mobile modal)
    const tickerSymbol = getTickerSymbol(baseAssetSymbol, QUOTE_TOKEN_SYMBOL)
    const { marketPrice } = useMarket(tickerSymbol)

    const clearingHouseRef = useRef<ClearingHouse>()
    /* ========== STATUS HANDLERS ========== */
    const { getHandlers } = Handlers.useContainer()
    const { onError } = getHandlers()
    const { isTxLoading, setTxLoading } = TxLoading.useContainer()
    const { getTxNotifiers } = TxNotifiers.useContainer()
    const { onSuccess } = getTxNotifiers()
    const { notifySuccess, notifyError } = useNotification()

    /* ========== INPUT ========== */
    const [positionDraft, setPositionDraft] = useState<PositionDraft>()
    const [side, setSide] = useState<PositionSide>(sideDefault)
    const [quoteTokenAmountString, setQuoteTokenAmountString] = useState("")
    const [baseTokenAmountString, setBaseTokenAmountString] = useState("")
    const [buyingPowerRatio, setBuyingPowerRatio] = useState(DEFAULT_BUYING_POWER_RATIO)

    /* ========== PERIPHERAL ========== */
    const [buyingPower, setBuyingPower] = useState<Big>()
    const [entryPrice, setEntryPrice] = useState<Big>()
    const [isUsingOtcEntryPrice, setIsUsingOtcEntryPrice] = useState<boolean>(false)
    const [priceImpact, setPriceImpact] = useState<Big>()
    const [transactionFee, setTransactionFee] = useState<Big>()

    /* ========== STATUS ========== */
    const [error, setError] = useState<SDKError | undefined>()
    const [_successCount, setSuccessCount] = useState(0)

    const resetInputsAndInformation = useCallback(() => {
        setQuoteTokenAmountString("")
        setBaseTokenAmountString("")
        setError(undefined)
        setEntryPrice(undefined)
        setPriceImpact(undefined)
        setTransactionFee(undefined)
    }, [])

    const resetInputValues = useCallback(() => {
        setSide(sideDefault)
        setBuyingPowerRatio(DEFAULT_BUYING_POWER_RATIO)
        setBuyingPower(undefined)
        resetInputsAndInformation()
    }, [resetInputsAndInformation, sideDefault])

    const quoteTokenAmount = useMemo(() => strToBig(quoteTokenAmountString), [quoteTokenAmountString])
    const baseTokenAmount = useMemo(() => strToBig(baseTokenAmountString), [baseTokenAmountString])

    const handleSuccess = useCallback(() => {
        setSuccessCount(prev => ++prev)
    }, [])

    /* ========== OUTOUT BOTH PRICES FOR UI ========== */
    const [otcEntryPrice, setOtcEntryPrice] = useState<Big>()
    const [coreEntryPrice, setCoreEntryPrice] = useState<Big>()

    // NOTE: Create PositionDraft ONCE for the entire editing lifecycle. Only recreate when success or market change.
    usePerpInitializedEffect(
        perp => {
            const { clearingHouse } = perp
            resetInputValues()

            const newPositionDraft = clearingHouse.createPositionDraft({
                tickerSymbol,
                side: sideDefault,
                amountInput: BIG_ZERO,
                isAmountInputBase: DEFAULT_LAST_EDIT_BASE,
            })
            clearingHouseRef.current = clearingHouse
            setPositionDraft(newPositionDraft)
        },
        [tickerSymbol, _successCount], // NOTE: dep on successCount to recreate whenever succeeded.
    )

    const { appChainId } = AppNetwork.useContainer()
    const { accountChainId, signer, account } = Web3WalletContainer.useContainer()
    const segmentUserId = segmentService.getUserId()

    usePerpInitializedEffect(
        perp => {
            if (!positionDraft) {
                return
            }

            // TODO: [429 issue] check if we can reduce contract calls
            const unsubscribePositionDraftUpdated = positionDraft.on(
                "updated",
                async (positionDraft: PositionDraft) => {
                    try {
                        const otcMakerClient = new OtcMakerClient()
                        const isLong = positionDraft.side === PositionSide.LONG
                        const [marketPrice, coreEntryPrice, corePriceImpact, transactionFee, coreSwap, otcQuoteResult] =
                            await Promise.all([
                                positionDraft.market.getPrice("marketPrice"),
                                positionDraft.getEntryPrice(),
                                positionDraft.getPriceImpact(),
                                positionDraft.getTransactionFee(),
                                positionDraft.getSwap(),
                                otcMakerClient.quoteWithTimeout(
                                    appChainId,
                                    positionDraft.market.baseAddress,
                                    isLong,
                                    positionDraft.amountInput,
                                    positionDraft.isAmountInputBase,
                                    segmentUserId,
                                    5000,
                                ),
                            ])
                        const openPositionService = new OpenPositionService()
                        // use core price as default
                        let isCorePriceBetter = true
                        let entryPrice = coreEntryPrice
                        let priceImpact = corePriceImpact

                        if (otcQuoteResult.isSuccess && otcQuoteResult.quotePrice) {
                            const otcEntryPrice = Big(otcQuoteResult.quotePrice)
                            setOtcEntryPrice(otcEntryPrice)

                            // both prices do not consider trading fee
                            isCorePriceBetter = openPositionService.isCorePriceBetter(
                                isLong,
                                otcEntryPrice,
                                coreEntryPrice,
                            )
                            if (!isCorePriceBetter) {
                                entryPrice = otcEntryPrice
                                priceImpact = otcEntryPrice.div(marketPrice).sub(1)
                            }
                        } else {
                            setOtcEntryPrice(undefined)
                        }

                        if (positionDraft.isAmountInputBase) {
                            const quoteAmount = isCorePriceBetter
                                ? coreSwap.output // output already includes fee
                                : positionDraft.amountInput.mul(
                                      openPositionService.calcOtcMakerPriceWithFee(isLong, entryPrice),
                                  )
                            setQuoteTokenAmountString(quoteAmount.toString())
                        } else {
                            const baseAmount = isCorePriceBetter
                                ? coreSwap.output // output already includes fee
                                : positionDraft.amountInput.div(
                                      openPositionService.calcOtcMakerPriceWithFee(isLong, entryPrice),
                                  )
                            setBaseTokenAmountString(baseAmount.toString())
                        }

                        setIsUsingOtcEntryPrice(!isCorePriceBetter)
                        setEntryPrice(entryPrice)
                        setPriceImpact(priceImpact)
                        setTransactionFee(transactionFee)
                        setCoreEntryPrice(coreEntryPrice)

                        if (perp.hasConnected() && positionDraft.amountInput.gt(0)) {
                            await positionDraft.simulateOpenPosition()
                        }
                    } catch (error: any) {
                        setError(error)
                        onError(error)
                    }
                },
            )

            const unsubscribeError = positionDraft.on("updateError", ({ error }) => {
                setError(error)
                onError(error)
            })

            return () => {
                unsubscribePositionDraftUpdated()
                unsubscribeError()
            }
        },
        [positionDraft],
    )

    usePerpConnectedEffect(
        perp => {
            if (!positionDraft || !perp.hasConnected()) {
                return
            }

            const fetchBuyingPower = async () => {
                try {
                    const buyingPower = await positionDraft.getBuyingPower()
                    setBuyingPower(buyingPower)
                } catch (error: any) {
                    setError(error)
                    onError(error)
                }
            }
            const unsubscribe = positionDraft.on("buyingPowerUpdated", fetchBuyingPower)

            return () => {
                unsubscribe()
            }
        },
        [positionDraft],
    )

    // NOTE: update buyingPowerRatio
    useEffect(() => {
        if (!buyingPower || !quoteTokenAmount) {
            return
        }
        setBuyingPowerRatio(buyingPower.lte(0) ? 0 : quoteTokenAmount.div(buyingPower).toNumber())
    }, [buyingPower, quoteTokenAmount])

    // FIXME: should make a `useDebounce` hook with build-in `useCallback` ability instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateDraftPosition = useCallback(
        debounce((params: PositionDraftDataUpdatable) => {
            if (!positionDraft || !clearingHouseRef.current) {
                return
            }

            setPositionDraft(
                clearingHouseRef.current.createPositionDraft({
                    tickerSymbol,
                    side: params.side || positionDraft.side,
                    amountInput: params.amountInput || positionDraft.amountInput,
                    isAmountInputBase: params.isAmountInputBase ?? positionDraft.isAmountInputBase,
                }),
            )
        }, DEBOUNCE_SECONDS),
        [positionDraft],
    )

    const updateAmountBase = useCallback(
        (amountInput: string) => {
            setBaseTokenAmountString(amountInput)
            setQuoteTokenAmountString("")
            setError(undefined)
            if (amountInput !== "") {
                debouncedUpdateDraftPosition({ amountInput: strToBig(amountInput), isAmountInputBase: true })
            } else {
                resetInputsAndInformation()
            }
        },
        [debouncedUpdateDraftPosition, resetInputsAndInformation],
    )

    const updateAmountQuote = useCallback(
        (amountInput: string) => {
            setQuoteTokenAmountString(amountInput)
            setBaseTokenAmountString("")
            setError(undefined)
            if (amountInput !== "") {
                debouncedUpdateDraftPosition({ amountInput: strToBig(amountInput), isAmountInputBase: false })
            } else {
                resetInputsAndInformation()
            }
        },
        [debouncedUpdateDraftPosition, resetInputsAndInformation],
    )

    const updateBuyingPowerRatio = useCallback(
        (ratio: number) => {
            if (!positionDraft || !buyingPower) {
                return
            }
            const isAmountInputBase = false
            const amountQuote = buyingPower.mul(ratio)
            setQuoteTokenAmountString(amountQuote.toString())
            setBaseTokenAmountString("")
            setBuyingPowerRatio(ratio)
            setError(undefined)
            debouncedUpdateDraftPosition({ amountInput: amountQuote, isAmountInputBase })
        },
        [buyingPower, positionDraft, debouncedUpdateDraftPosition],
    )

    const toggleSide = useCallback(() => {
        if (!positionDraft) {
            return
        }
        const sideNext = positionDraft.side === PositionSide.LONG ? PositionSide.SHORT : PositionSide.LONG
        debouncedUpdateDraftPosition({ side: sideNext })
        setError(undefined)
        setSide(sideNext)
    }, [positionDraft, debouncedUpdateDraftPosition])

    const openCorePosition = usePerpTransactionSender<OpenPositionParams>(
        ({ clearingHouse }, { positionDraft, slippage, referralCode }) => {
            return clearingHouse.openPosition(positionDraft, slippage, referralCode)
        },
        [],
    )

    const openOtcMakerPosition = useCallback(
        async (params: OpenPositionParams) => {
            if (!entryPrice || !signer || !account || !accountChainId) {
                return
            }
            const otcMakerClient = new OtcMakerClient()
            const openPositionService = new OpenPositionService()
            const { positionDraft, slippage, referralCode } = params

            const isLong = positionDraft.side === PositionSide.LONG
            const slippageWithFee = slippage.add(0.001)
            const oppositeAmountBound = openPositionService.calcOppositeAmountBound(
                isLong,
                positionDraft.amountInput,
                positionDraft.isAmountInputBase,
                entryPrice,
                slippageWithFee,
            )

            const limitOrderParams = {
                trader: account,
                baseToken: positionDraft.market.baseAddress,
                orderType: 3,
                amount: toWei(positionDraft.amountInput),
                oppositeAmountBound: toWei(oppositeAmountBound),
                isBaseToQuote: positionDraft.isBaseToQuote,
                isExactInput: positionDraft.isExactInput,
                salt: Math.floor(Math.random() * 1000),
                deadline: Math.floor(Date.now() / 1000 + 86400),
                reduceOnly: false,
                sqrtPriceLimitX96: Big(0),
                referralCode: utils.formatBytes32String(referralCode || ""),
                roundIdWhenCreated: Big(0),
                triggerPrice: Big(0),
            }

            try {
                setTxLoading(true)
                const signature = await otcMakerClient.sign(signer, limitOrderParams)
                const { isSuccess, txHash, message } = await otcMakerClient.openPosition(
                    accountChainId,
                    limitOrderParams,
                    signature,
                )
                if (isSuccess && txHash) {
                    // TODO: we need to update the graph to prevent updating the limit order history
                    notifySuccess({
                        title: "Smart Maker Transaction Sent Successfully",
                        description: "Positions should be updated shortly.",
                        duration: 10 * 1000,
                    })
                    handleSuccess()
                } else {
                    notifyError({ title: "Smart Maker Transaction Failed", description: message })
                }
            } catch (rawError: any) {
                const error = new Error(JSON.stringify({ error: rawError, limitOrderParams }))
                if (isUserDeniedSignature(rawError)) {
                    notifyError({ title: "Signing Smart Maker Transaction Rejected" })
                } else {
                    notifyError({ title: "Smart Maker Transaction Failed" })
                    onError(error)
                }
            } finally {
                setTxLoading(false)
            }
        },
        [entryPrice, signer, account, accountChainId, handleSuccess, notifyError, notifySuccess, onError, setTxLoading],
    )

    const openPosition = useCallback<(params: OpenPositionParams) => void>(
        ({ ...args }) => {
            if (args.isUsingOtcEntryPrice) {
                openOtcMakerPosition(args)
            } else {
                openCorePosition({
                    ...args,
                    options: {
                        onSuccess: (data, contractFunctionName) => {
                            onSuccess(data, contractFunctionName)
                            handleSuccess()
                        },
                    },
                })
            }
        },
        [openCorePosition, onSuccess, handleSuccess, openOtcMakerPosition],
    )

    const isInputReady = useMemo(() => {
        return !!side && quoteTokenAmount?.gt(0) && baseTokenAmount?.gt(0)
    }, [baseTokenAmount, quoteTokenAmount, side])

    const { slippage } = Slippage.useContainer()
    const { referralCode } = Referral.useContainer()

    const handleSubmit = useCallback(() => {
        if (!positionDraft) {
            return
        }
        trackOpenPositionButtonClicked({
            slippage: slippage.toString(),
            positionDraft,
            buyingPowerRatio,
            isUsingOtcEntryPrice,
        })
        return openPosition({ isUsingOtcEntryPrice, positionDraft, slippage, referralCode })
    }, [isUsingOtcEntryPrice, positionDraft, slippage, buyingPowerRatio, openPosition, referralCode])

    return {
        positionDraft,

        // NOTE: Inputs
        marketPriceString: marketPrice?.toString(),
        side,
        baseTokenAmountString,
        quoteTokenAmountString,
        baseTokenAmount,
        quoteTokenAmount,
        buyingPowerRatio,

        // NOTE: Handlers
        toggleSide,
        updateAmountBase,
        updateAmountQuote,
        updateBuyingPowerRatio,
        handleSubmit,

        // NOTE: Peripheral
        baseAssetSymbol,
        buyingPower,

        // NOTE: Status
        isInputReady,
        isTxLoading,
        error,

        // NOTE: positionDraft v2
        entryPrice,
        isUsingOtcEntryPrice,
        priceImpact,
        transactionFee,

        // NOTE: both prices for UI
        coreEntryPrice,
        otcEntryPrice,
    }
}
